import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin } from "lucide-react";
import { Reveal, PageHero, Eyebrow } from "@/components/Motion";
import { useLanguage } from "@/i18n";

export default function Progetti() {
  const [lightbox, setLightbox] = useState(null);
  const { t, content } = useLanguage();

  return (
    <main data-testid="progetti-page">
      <PageHero
        eyebrow={t("progettiHeroEyebrow")}
        lines={[t("progettiL1"), t("progettiL2")]}
        sub={t("progettiSub")}
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32">
        <div className="space-y-24 md:space-y-36">
          {content.projects.map((project, pIdx) => (
            <motion.article
              key={project.id}
              data-testid={`project-${project.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex flex-col justify-between gap-4 border-b border-stone-300 pb-8 md:flex-row md:items-end">
                <div className="flex items-start gap-6">
                  <span className="font-serif text-5xl italic text-[#8F6B3C]/60 md:text-6xl">
                    {project.index}
                  </span>
                  <div>
                    <h2 className="font-serif text-3xl uppercase leading-tight tracking-tight text-stone-900 md:text-4xl">
                      {project.title}
                    </h2>
                    <p className="mt-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-500">
                      <MapPin className="h-3.5 w-3.5 text-[#8F6B3C]" />
                      {project.location} · {project.category} · {project.year}
                    </p>
                  </div>
                </div>
                <p className="max-w-md text-sm leading-relaxed text-stone-500">{project.desc}</p>
              </div>

              <div className="mt-10 grid gap-5 md:grid-cols-12">
                {project.images.map((img, i) => {
                  const spans =
                    project.images.length === 2
                      ? ["md:col-span-7", "md:col-span-5"]
                      : project.images.length === 5
                      ? ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4"]
                      : ["md:col-span-6", "md:col-span-6"];
                  return (
                    <motion.button
                      key={i}
                      data-testid={`project-${project.id}-img-${i}`}
                      onClick={() => setLightbox({ src: img, alt: `${project.title} — ${t("photo")} ${i + 1}` })}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                      className={`group overflow-hidden ${spans[i] || "md:col-span-6"}`}
                    >
                      <img
                        src={img}
                        alt={`${project.title} — ${t("photo")} ${i + 1}`}
                        className="img-duotone aspect-square w-full cursor-zoom-in object-cover"
                        loading="lazy"
                      />
                    </motion.button>
                  );
                })}
              </div>
            </motion.article>
          ))}
        </div>

        <Reveal className="mt-24 text-center md:mt-32">
          <Eyebrow className="justify-center">{t("nextEyebrow")}</Eyebrow>
          <p className="mx-auto mt-4 max-w-xl font-serif text-3xl uppercase leading-tight text-stone-900 md:text-4xl">
            {t("nextTitleA")} <span className="italic text-[#8F6B3C]">{t("nextTitleB")}</span>
          </p>
          <a
            data-testid="projects-cta-whatsapp"
            href={`https://wa.me/393208908036?text=${encodeURIComponent(t("waProjectsMsg"))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 whitespace-nowrap bg-[#C89D66] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.15em] text-[#0F1012] transition-colors duration-300 hover:bg-[#D4A972]"
          >
            {t("projectsCta")}
          </a>
        </Reveal>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#F7F5F0]/95 p-6 backdrop-blur-md"
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Chiudi"
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-stone-300 text-stone-800 transition-colors hover:border-[#C89D66] hover:text-[#8F6B3C]"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-h-[85vh] max-w-full object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
